import React, { useState, useEffect, useRef } from "react";
import { FaShoppingCart, FaHeart, FaUser, FaSearch } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Header.css";
import { useAuth } from "../contexts/AuthContext";
import LoginModal from "../pages/login";

// --- Global search data ---
const productData = [
  {
    id: 1,
    name: "Kids Soft Toothbrush",
    description:
      "Perfect for children's sensitive teeth and gums. Recommended for ages 3-6.",
    category: "children",
    path: "/shop/children",
  },
  {
    id: 2,
    name: "Children's Medium Bristle",
    description:
      "Designed for kids 5-12 years. Helps develop good brushing habits.",
    category: "children",
    path: "/shop/children",
  },
  {
    id: 3,
    name: "Kids Special Brush",
    description:
      "Fun design for young children with extra-soft bristles for gentle cleaning.",
    category: "children",
    path: "/shop/children",
  },
  {
    id: 4,
    name: "Soft Adult Brush",
    description: "Gentle on gums with advanced cleaning technology for adults.",
    category: "adult",
    path: "/shop/adult",
  },
  {
    id: 5,
    name: "Medium Adult Brush",
    description: "Perfect balance of cleaning power and comfort for daily use.",
    category: "adult",
    path: "/shop/adult",
  },
  {
    id: 6,
    name: "Hard Adult Brush",
    description: "Deep cleaning action for adults who prefer firmer bristles.",
    category: "adult",
    path: "/shop/adult",
  },
  {
    id: 7,
    name: "Soft Old Age Brush",
    description:
      "Extra soft bristles specially designed for sensitive gums and teeth.",
    category: "old-age",
    path: "/shop/old-age",
  },
  {
    id: 8,
    name: "Medium Old Age Brush",
    description:
      "Comfortable grip handle with medium-soft bristles for better control.",
    category: "old-age",
    path: "/shop/old-age",
  },
  {
    id: 9,
    name: "Special Old Age Brush",
    description:
      "Easy-grip design perfect for those with arthritis or limited mobility.",
    category: "old-age",
    path: "/shop/old-age",
  },
];
const categoryData = [
  {
    id: "children",
    name: "Children's Collection",
    description:
      "Specially designed toothbrushes with soft bristles and fun colors to make brushing enjoyable for kids aged 3-12.",
    path: "/shop/children",
  },
  {
    id: "adult",
    name: "Adult Premium Series",
    description:
      "Professional-grade toothbrushes with precision-engineered bristles for comprehensive oral care and plaque removal.",
    path: "/shop/adult",
  },
  {
    id: "old age",
    name: "Essential Care Line",
    description:
      "Ultra-gentle toothbrushes designed for sensitive teeth and gums, providing effective cleaning without irritation.",
    path: "/shop/old-age",
  },
];
const staticPages = [
  {
    name: "Home",
    description: "Welcome to Banat - premium toothbrushes for all ages.",
    path: "/",
  },
  {
    name: "About",
    description: "About Banat and our mission.",
    path: "/about",
  },
  {
    name: "Contact",
    description: "Contact Banat for support or inquiries.",
    path: "/contact",
  },
];

const Header = ({ cartItems = [], wishlist = [] }) => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [globalSearch, setGlobalSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profileRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Check if we're on admin dashboard
  const isAdminDashboard = location.pathname.startsWith("/admin-dashboard");

  const cartItemsCount = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );

  const allSearchData = [...productData, ...categoryData, ...staticPages];

  const filteredResults = globalSearch.trim()
    ? allSearchData.filter(
        (item) =>
          (item.name &&
            item.name.toLowerCase().includes(globalSearch.toLowerCase())) ||
          (item.description &&
            item.description.toLowerCase().includes(globalSearch.toLowerCase()))
      )
    : [];

  const handleResultClick = (path) => {
    setShowDropdown(false);
    setGlobalSearch("");
    navigate(path);
  };

  const handleLoginClick = (e) => {
    e.stopPropagation();
    setIsSignUp(false);
    setShowLoginModal(true);
  };

  const handleWishlistClick = () => {
    navigate("/wishlist");
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // No drawer, so no overflow prevention effect is needed

  // Click outside to close profile dropdown (Desktop view) and mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleProfileDropdown = (e) => {
    e.stopPropagation();
    setProfileDropdown(!profileDropdown);
  };

  return (
    <header className="header">
      <div className="header-container">
        {/* Logo - Left side */}
        <Link to="/" className="logo">
          <img src="/banat logo .png" alt="Banat Logo" />
        </Link>

        {/* Search Bar - Center */}
        {!isAdminDashboard && (
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search products, categories, pages..."
              className="search-input"
              value={globalSearch}
              onChange={(e) => {
                setGlobalSearch(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              autoComplete="off"
            />
            {showDropdown && filteredResults.length > 0 && (
              <div className="global-search-dropdown">
                {filteredResults.map((item, idx) => (
                  <div
                    key={idx}
                    className="global-search-result"
                    onMouseDown={() => handleResultClick(item.path)}
                  >
                    <div className="global-search-title">{item.name}</div>
                    <div className="global-search-desc">
                      {item.description}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Icons - Right side (Desktop only) */}
        <div className={`header-right ${isAdminDashboard ? "admin-dashboard" : ""}`}>
          {!isAdminDashboard && (
            <>
              {/* Wishlist Icon */}
              <div
                className="icon-container"
                onClick={handleWishlistClick}
                style={{ cursor: "pointer" }}
              >
                <FaHeart className="icon" />
                <span className="icon-label">Wishlist</span>
                {wishlist.length > 0 && (
                  <span className="cart-count wishlist-count">
                    {wishlist.length}
                  </span>
                )}
              </div>

              {/* Cart Icon */}
              <Link to="/cart" className="icon-container cart-link">
                <FaShoppingCart className="icon" />
                <span className="icon-label">Cart</span>
                {cartItemsCount > 0 && (
                  <span className="cart-count">{cartItemsCount}</span>
                )}
              </Link>
            </>
          )}

          {/* Profile Icon */}
          <div
            ref={profileRef}
            className="icon-container profile-container"
            onClick={toggleProfileDropdown}
          >
            <FaUser className="icon" />
            {currentUser && (
              <span className="profile-name">
                {currentUser.displayName || currentUser.email.split("@")[0]}
              </span>
            )}
            <span className="icon-label">Profile</span>

            {profileDropdown && (
              <div className="profile-dropdown profile-dropdown-card">
                <div
                  className="banat-login-logo banat-logo-amazon-style"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <img
                    src="/banat logo .png"
                    alt="Banat"
                    className="banat-logo"
                    style={{ height: 32 }}
                  />
                </div>

                {!currentUser ? (
                  <>
                    <div className="profile-dropdown-welcome">
                      Welcome to Banat
                    </div>
                    <div className="profile-dropdown-message">
                      Sign in to access your account
                    </div>
                    <button
                      className="profile-dropdown-login-btn"
                      onClick={handleLoginClick}
                    >
                      Sign In
                    </button>
                    <div className="profile-dropdown-divider"></div>
                    <div className="profile-dropdown-link">
                      <Link to="/signup">New customer? Start here</Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="profile-dropdown-welcome">
                      Welcome back!
                    </div>
                    <div className="profile-dropdown-message">
                      {currentUser.displayName || currentUser.email}
                    </div>
                    <div className="profile-dropdown-divider"></div>
                    <div className="profile-dropdown-link">
                      <Link
                        to="/dashboard"
                        onClick={() => setProfileDropdown(false)}
                      >
                        My Account
                      </Link>
                    </div>
                    <div className="profile-dropdown-link">
                      <Link
                        to="/orders"
                        onClick={() => setProfileDropdown(false)}
                      >
                        My Orders
                      </Link>
                    </div>
                    <div className="profile-dropdown-link">
                      <Link
                        to="/wishlist"
                        onClick={() => setProfileDropdown(false)}
                      >
                        My Wishlist
                      </Link>
                    </div>
                    <div className="profile-dropdown-divider"></div>
                    <button
                      className="profile-dropdown-login-btn"
                      onClick={handleSignOut}
                    >
                      Sign Out
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

        </div>

        {/* Hamburger Button - Mobile only */}
        {!isAdminDashboard && (
          <div ref={mobileMenuRef} style={{ position: "relative" }}>
            <button
              className={`hamburger-btn ${mobileMenuOpen ? "open" : ""}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>

            {/* Mobile Dropdown Menu */}
            <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
              {/* Wishlist */}
              <div
                className="mobile-menu-item"
                onClick={() => { setMobileMenuOpen(false); navigate("/wishlist"); }}
              >
                <FaHeart className="mobile-menu-icon" style={{ color: "#e53e3e" }} />
                {wishlist.length > 0 && (
                  <span className="mobile-badge">{wishlist.length}</span>
                )}
                Wishlist
              </div>

              {/* Cart */}
              <Link
                to="/cart"
                className="mobile-menu-item"
                onClick={() => setMobileMenuOpen(false)}
              >
                <FaShoppingCart className="mobile-menu-icon" style={{ color: "#ff4374" }} />
                {cartItemsCount > 0 && (
                  <span className="mobile-badge cart">{cartItemsCount}</span>
                )}
                Cart
              </Link>

              <div className="mobile-menu-divider"></div>

              {/* Profile / Account */}
              {!currentUser ? (
                <div
                  className="mobile-menu-item"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setIsSignUp(false);
                    setShowLoginModal(true);
                  }}
                >
                  <FaUser className="mobile-menu-icon" style={{ color: "#00b5ad" }} />
                  Sign In
                </div>
              ) : (
                <>
                  <Link
                    to="/dashboard"
                    className="mobile-menu-item"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <FaUser className="mobile-menu-icon" style={{ color: "#00b5ad" }} />
                    My Account
                  </Link>
                  <Link
                    to="/orders"
                    className="mobile-menu-item"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <FaUser className="mobile-menu-icon" style={{ color: "#64748b" }} />
                    My Orders
                  </Link>
                  <div className="mobile-menu-divider"></div>
                  <div
                    className="mobile-menu-item"
                    onClick={() => { setMobileMenuOpen(false); handleSignOut(); }}
                    style={{ color: "#e53e3e" }}
                  >
                    <FaUser className="mobile-menu-icon" />
                    Sign Out
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <LoginModal
              onClose={() => setShowLoginModal(false)}
              initialMode={isSignUp}
            />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
